import { TreeNode } from 'primeng/api'

import {
  DataModelAttributeType,
  DataModelEntityType,
  EnumReferenceInfo,
  TreeNodeData,
} from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { getStyleClassByType } from '@flowx/designer/modules/data-model-builder/services/data-model-builder.utils'

export type NewAttributeRequest = {
  path: string
  type?: DataModelAttributeType
  enumReference?: EnumReferenceInfo
  numberOfDecimals?: number
}

export type DataMapperSuggestion = {
  path: string
  reason?: string
  typeLabel?: string
  dummyEntry?: boolean
  locked?: boolean
}

const READ_ONLY_LEGEND_CLASS = getStyleClassByType(DataModelEntityType.PROJECT)

const READ_ONLY_REASON = 'Cannot add attributes under a Project/Library data type'
const EMPTY_SEGMENT_REASON = 'Name an attribute after the dot'
const NOT_AN_OBJECT_REASON = 'Cannot add attributes under a primitive attribute'

export const CREATABLE_TYPES: ReadonlySet<DataModelAttributeType> = new Set([
  DataModelAttributeType.STRING,
  DataModelAttributeType.INTEGER,
  DataModelAttributeType.FLOAT,
  DataModelAttributeType.BOOLEAN,
  DataModelAttributeType.DATE_TIME,
  DataModelAttributeType.DATE_TIME_ZONED,
  DataModelAttributeType.ENUM,
  DataModelAttributeType.CURRENCY,
])

export function canCreateForNode(node: TreeNode<TreeNodeData>): boolean {
  const { type, multiInstance } = node?.data?.entity ?? {}

  return !multiInstance && CREATABLE_TYPES.has(type as DataModelAttributeType)
}

export function unwrapTypedPath(query: string): string {
  return query.startsWith('${') ? query.slice(2).replace(/}$/, '') : query
}

export function buildSuggestions(
  typedPath: string,
  tree: TreeNode<TreeNodeData>[],
  canAdd: boolean
): DataMapperSuggestion[] {
  const matches = collectSuggestions(typedPath.split('.'), tree, '')

  return [
    ...matches,
    ...newAttributeEntry(typedPath, tree, canAdd && !exactMatch(matches, typedPath)),
  ]
}

export function buildStaticSuggestions(
  value: string,
  tree: TreeNode<TreeNodeData>[],
  canAdd: boolean
): DataMapperSuggestion[] {
  const matches = collectSuggestions(value.split('.'), tree, '')

  return newAttributeEntry(value, tree, canAdd && !exactMatch(matches, value))
}

export function collectSuggestions(
  parts: string[],
  nodes: TreeNode<TreeNodeData>[],
  path: string
): DataMapperSuggestion[] {
  const [current, ...rest] = parts

  return (nodes ?? []).flatMap((node) => {
    const fullPath = path ? `${path}.${node.label}` : node.label
    const isSegment = node.label?.toLowerCase() === current.toLowerCase()

    if (rest.length) {
      return isSegment && node.children ? collectSuggestions(rest, node.children, fullPath) : []
    }

    return [
      ...(node.label?.toLowerCase().startsWith(current.toLowerCase())
        ? [{ path: fullPath, typeLabel: typeLabelOf(node) }]
        : []),
      ...(isSegment && node.children
        ? node.children.map((child) => ({
            path: `${fullPath}.${child.label}`,
            typeLabel: typeLabelOf(child),
          }))
        : []),
    ]
  })
}

export function createRejected(path: string, tree: TreeNode<TreeNodeData>[]): string | undefined {
  const segments = path.split('.')

  if (!segments.every(Boolean)) {
    return EMPTY_SEGMENT_REASON
  }

  const parentSegments = segments.slice(0, -1)

  if (!parentSegments.length) {
    return undefined
  }

  const anchor = nodesAlongPath(parentSegments, tree).at(-1)

  if (!anchor) {
    return undefined
  }

  if (!acceptsAttributes(anchor)) {
    return NOT_AN_OBJECT_REASON
  }

  return isReadOnly(anchor) ? READ_ONLY_REASON : undefined
}

export function acceptsAttributes(node: TreeNode<TreeNodeData>): boolean {
  return !!node?.children
}

export function typeLabelOf(node: TreeNode<TreeNodeData>): string {
  return node?.data?.state?.displayTypeObject?.typeContent ?? ''
}

export function nodesAlongPath(
  segments: string[],
  nodes: TreeNode<TreeNodeData>[]
): TreeNode<TreeNodeData>[] {
  const [segment, ...rest] = segments
  const node = segment
    ? nodes?.find((candidate) => candidate.label?.toLowerCase() === segment.toLowerCase())
    : undefined

  return node ? [node, ...nodesAlongPath(rest, node.children ?? [])] : []
}

function newAttributeEntry(
  path: string,
  tree: TreeNode<TreeNodeData>[],
  allowed: boolean
): DataMapperSuggestion[] {
  if (!path || !allowed) {
    return []
  }

  const reason = createRejected(path, tree)

  return [{ path, dummyEntry: true, locked: !!reason, ...(reason && { reason }) }]
}

function exactMatch(matches: DataMapperSuggestion[], path: string): boolean {
  return matches.some((match) => match.path === path)
}

function isReadOnly(node: TreeNode<TreeNodeData>): boolean {
  return !!node?.styleClass?.split(' ').includes(READ_ONLY_LEGEND_CLASS)
}
